import { Alert, Button, Grid, Stack, Typography } from '@mui/material';
import { PlatformVersionPolicySelect } from 'components/StudiesDashboard/StudySettings/PlatformSettings';
import { PostIngestPipelineSettings } from 'components/StudiesDashboard/StudySettings/PostIngestPipelineSettings';
import { WorkflowConfigurationPanel } from 'components/StudyDashboard/Pipelines/Multiplex/WorkflowConfigurationPanel';
import {
  getStudyWorkflowConfiguration,
  updateStudyWorkflowConfiguration,
} from 'components/StudyDashboard/Workflow/studyWorkflowConfiguration';
import { Permission } from 'interfaces/permissionOption';
import { PlatformVersionPolicy, Study } from 'interfaces/study';
import { isEqual } from 'lodash';
import React, { useState } from 'react';
import { getQueryErrorMessage } from 'utils/httpError';
import { usePermissions } from 'utils/usePermissions';

interface LabStudyDefaultsSettingsProps {
  currentLabStudyDefaultSettings?: Study['settings'];
  onSave: (settings: Study['settings']) => Promise<unknown>;
}

export const LabStudyDefaultsSettings: React.FC<LabStudyDefaultsSettingsProps> = ({
  currentLabStudyDefaultSettings,
  onSave,
}) => {
  const [unsavedSettings, setSettings] = useState<Study['settings'] | undefined>(undefined);
  const [isSaving, setIsSaving] = useState(false);
  const [error, setError] = useState<unknown>();
  const settings = unsavedSettings || currentLabStudyDefaultSettings;
  const hasChanges = !isEqual(settings, currentLabStudyDefaultSettings);
  const workflowConfiguration = getStudyWorkflowConfiguration(settings?.workflow?.modules);
  const { hasPermission } = usePermissions();
  const canEditPlatformVersionPolicy = hasPermission(Permission.EditPlatformSettings);

  const updatePlatformVersionPolicy = (versionPolicy: PlatformVersionPolicy | undefined) => {
    setSettings((currentSettings) => ({
      ...(currentSettings ?? currentLabStudyDefaultSettings),
      platform: { ...(currentSettings ?? currentLabStudyDefaultSettings)?.platform, versionPolicy },
    }));
  };
  const updateWorkflowConfiguration = (key: keyof typeof workflowConfiguration, enabled: boolean) => {
    setSettings((currentSettings) => ({
      ...currentSettings,
      workflow: {
        ...currentSettings?.workflow,
        modules: updateStudyWorkflowConfiguration(
          getStudyWorkflowConfiguration(currentSettings?.workflow?.modules),
          key,
          enabled
        ),
      },
    }));
  };

  const saveDefaults = async () => {
    setIsSaving(true);
    setError(undefined);
    try {
      await onSave(settings);
      setSettings(undefined);
    } catch (saveError) {
      setError(saveError);
    } finally {
      setIsSaving(false);
    }
  };

  return (
    <Stack spacing={3} aria-label="study defaults settings">
      <Stack spacing={0.75}>
        <Typography variant="h6" sx={{ fontWeight: 600 }}>
          Study defaults
        </Typography>
        <Typography variant="body2" color="text.secondary">
          These settings apply to new studies. A study-level setting always takes precedence.
        </Typography>
      </Stack>
      <PostIngestPipelineSettings updatedSettings={settings} setUpdatedSettings={setSettings} />
      {canEditPlatformVersionPolicy ? (
        <Grid container>
          <PlatformVersionPolicySelect
            value={settings?.platform?.versionPolicy}
            onChange={updatePlatformVersionPolicy}
            helperText="Applies to every study in this lab without its own policy, including existing studies."
          />
        </Grid>
      ) : null}
      <WorkflowConfigurationPanel
        configuration={workflowConfiguration}
        onChange={updateWorkflowConfiguration}
        scope="lab-defaults"
      />
      {error ? (
        <Alert severity="error">{getQueryErrorMessage(error, 'Study defaults could not be saved.')}</Alert>
      ) : null}
      <Stack direction="row" justifyContent="flex-end">
        <Button variant="contained" onClick={saveDefaults} disabled={isSaving || !hasChanges}>
          {isSaving ? 'Saving…' : 'Save study defaults'}
        </Button>
      </Stack>
    </Stack>
  );
};
